// src/pages/tender/AdminBoqUpload.js
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";

const cx = (...xs) => xs.filter(Boolean).join(" ");

export default function AdminBoqUpload() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [tender, setTender] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overwrite, setOverwrite] = useState(true);

  // ✅ Admin guard (CIO/Admin/CEO – token.admin / token.role me hoga)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        toast.error("Please login as Admin.");
        navigate("/admin", { replace: true });
        return;
      }
      const token = await u.getIdTokenResult();
      const isAdmin =
        token.claims.admin === true ||
        ["CIO", "Admin", "CEO"].includes(token.claims.role);
      if (!isAdmin) {
        toast.error("Access denied – admin only.");
        navigate("/", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  // load tender info
  useEffect(() => {
    const loadTender = async () => {
      if (!tenderId) return;
      const tSnap = await getDoc(doc(db, "tenders", tenderId));
      if (!tSnap.exists()) {
        toast.error("Tender not found.");
        navigate("/admin", { replace: true });
        return;
      }
      setTender({ id: tSnap.id, ...tSnap.data() });
    };
    loadTender();
  }, [tenderId, navigate]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]]; // first sheet

      // JSON banayenge – header row me exact Excel headings honi chahiye:
      // "Item No.", "Item Description", "Unit", "Qty. Estimated", "Rate (Rs.)", "Rate with 1% LC"
      const json = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
        raw: true,
      });

      const parsed = json
        .map((row) => {
          const itemNo = Number(row["Item No."] || row["Item No"] || row["ITEM NO"] || row["Item"]);
          if (!itemNo) return null;

          return {
            itemNo,
            description:
              String(row["Item Description"] || row["Description"] || "").trim(),
            unit: String(row["Unit"] || row["UNIT"] || "").trim(),
            boqQty: Number(row["Qty. Estimated"] || row["Quantity"] || 0),
            rate: Number(row["Rate (Rs.)"] || row["Rate"] || 0),
            rateWithLC:
              Number(row["Rate with 1% LC"] || row["Rate with LC"] || 0),
          };
        })
        .filter(Boolean);

      if (!parsed.length) {
        toast.error("No valid BOQ rows found in Excel.");
        return;
      }

      setRows(parsed);
      toast.success(`Loaded ${parsed.length} BOQ rows from Excel.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to read Excel file.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToFirestore = async () => {
    if (!rows.length || !tenderId) {
      toast.error("No BOQ rows to save.");
      return;
    }
    try {
      setLoading(true);

      const colRef = collection(db, "tenders", tenderId, "boq_items");

      // optional: overwrite – pehle ke saare docs delete
      if (overwrite) {
        const snap = await getDocs(colRef);
        const delPromises = snap.docs.map((d) => deleteDoc(d.ref));
        await Promise.all(delPromises);
      }

      const writePromises = rows.map((row) => {
        // doc id ko itemNo ke hisab se rakh denge – easy debugging
        const id = String(row.itemNo).padStart(2, "0");
        return setDoc(doc(colRef, id), row, { merge: true });
      });

      await Promise.all(writePromises);
      toast.success("BOQ items saved for this tender.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save BOQ to Firestore.");
    } finally {
      setLoading(false);
    }
  };

  if (!user || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-sm text-slate-600">Loading…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-slate-50 pb-10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Admin · BOQ Upload
              </div>
              <h1 className="text-xl font-extrabold text-slate-900">
                {tender.title || tender.projectName || "Project"} – BOQ Master
              </h1>
              <div className="text-[11px] text-slate-600 mt-1">
                Tender ID: {tenderId}
              </div>
            </div>
            <Link
              to="/admin"
              className="text-xs px-3 py-1 rounded-lg bg-slate-900 text-white shadow"
            >
              ← Back to Admin Dashboard
            </Link>
          </div>

          {/* Upload card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
            <h2 className="text-sm font-bold text-slate-800 mb-2">
              1. Upload BOQ Excel (Project Wise)
            </h2>
            <p className="text-[11px] text-slate-600 mb-2">
              Excel me header ye hone chahiye:{" "}
              <b>
                Item No., Item Description, Unit, Qty. Estimated, Rate (Rs.),
                Rate with 1% LC
              </b>
              . System automatically saare rows read karega – extra / kam items
              dono handle ho jayenge.
            </p>

            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="text-xs"
            />

            <div className="mt-3 flex items-center gap-3">
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
                Overwrite existing BOQ items for this tender
              </label>

              <button
                type="button"
                onClick={handleSaveToFirestore}
                disabled={!rows.length || loading}
                className={cx(
                  "ml-auto px-4 py-2 rounded-lg text-xs font-semibold text-white",
                  !rows.length || loading
                    ? "bg-emerald-300 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700"
                )}
              >
                {loading ? "Saving…" : "Save BOQ to Firestore"}
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h2 className="text-sm font-bold text-slate-800 mb-2">
              2. Preview (from Excel)
            </h2>
            {rows.length === 0 ? (
              <div className="text-[12px] text-slate-500">
                Upload Excel to see BOQ preview.
              </div>
            ) : (
              <div className="overflow-auto max-h-[480px]">
                <table className="min-w-full text-[11px] border border-slate-200">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-200 px-2 py-1 text-left">
                        Item
                      </th>
                      <th className="border border-slate-200 px-2 py-1 text-left">
                        Description
                      </th>
                      <th className="border border-slate-200 px-2 py-1 text-left">
                        Unit
                      </th>
                      <th className="border border-slate-200 px-2 py-1 text-right">
                        BOQ Qty
                      </th>
                      <th className="border border-slate-200 px-2 py-1 text-right">
                        Rate
                      </th>
                      <th className="border border-slate-200 px-2 py-1 text-right">
                        Rate+1%LC
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.itemNo}>
                        <td className="border border-slate-200 px-2 py-1">
                          {r.itemNo}
                        </td>
                        <td className="border border-slate-200 px-2 py-1">
                          {r.description}
                        </td>
                        <td className="border border-slate-200 px-2 py-1">
                          {r.unit}
                        </td>
                        <td className="border border-slate-200 px-2 py-1 text-right">
                          {r.boqQty}
                        </td>
                        <td className="border border-slate-200 px-2 py-1 text-right">
                          {r.rate}
                        </td>
                        <td className="border border-slate-200 px-2 py-1 text-right">
                          {r.rateWithLC}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
